import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Code, Copy, CheckCircle2, Terminal, Package, Cpu, Globe, Zap, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SDK_PYTHON = `# pip install blackorex-sdk
from blackorex import BlackOreClient

client = BlackOreClient(api_key="${'${YOUR_API_KEY}'}")

# Get all agents and their performance
agents = client.agents.list()
for agent in agents:
    print(f"{agent.id}: {agent.name} | P&L: {agent.current_pnl}")

# Get real-time signals
signals = client.signals.stream()
for signal in signals:
    print(f"[{signal.agent_id}] {signal.side} {signal.symbol} @ {signal.price}")
    print(f"  Strategy: {signal.metadata.strategy}")
    print(f"  Confidence: {signal.confidence:.0%}")

# Execute a trade via agent
order = client.orders.create(
    agent_id="AGT-03",
    symbol="BTC/USD",
    side="buy",
    amount=10000,
)
print(f"Order {order.id}: {order.status}")

# Portfolio & Performance
portfolio = client.portfolio.summary()
print(f"Net Worth: \${portfolio.net_worth:,.2f}")
print(f"Total P&L: \${portfolio.total_pnl:,.2f}")`;

const SDK_JAVASCRIPT = `// npm install @blackorex/sdk
import { BlackOreClient } from '@blackorex/sdk';

const client = new BlackOreClient({ apiKey: '${'${YOUR_API_KEY}'}' });

// Stream real-time signals via WebSocket
const ws = client.signals.connect();
ws.on('signal', (signal) => {
  console.log(\`[\${signal.agentId}] \${signal.side} \${signal.symbol}\`);
  console.log(\`  Strategy: \${signal.metadata.strategy}\`);
  console.log(\`  Confidence: \${(signal.confidence * 100).toFixed(0)}%\`);
});

// REST: Get agents
const agents = await client.agents.list();
agents.forEach(a => {
  console.log(\`\${a.id}: \${a.name} | Return: \${a.totalReturn}%\`);
});

// REST: Get trade journal with TCA
const journal = await client.journal.trades({
  limit: 50,
  agentId: 'AGT-03',
});
journal.trades.forEach(t => {
  console.log(\`\${t.side} \${t.symbol} P&L: \$\${t.pnl.toFixed(2)}\`);
  console.log(\`  Slippage: \${t.tca.slippageBps}bps\`);
});`;

const SDK_CURL = `# Authentication
curl -X POST ${BACKEND_URL}/api/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email": "you@example.com", "password": "your_password"}'

# Get all agents
curl ${BACKEND_URL}/api/agents \\
  -H "Cookie: token=\${TOKEN}"

# Get trade journal with explainability
curl "${BACKEND_URL}/api/journal/trades?limit=20&agent_id=AGT-03" \\
  -H "Cookie: token=\${TOKEN}"

# Get real-time signals
curl ${BACKEND_URL}/api/journal/signals?limit=10 \\
  -H "Cookie: token=\${TOKEN}"

# Run a trading cycle
curl -X POST ${BACKEND_URL}/api/trading/cycle \\
  -H "Cookie: token=\${TOKEN}" \\
  -H "Content-Type: application/json" \\
  -d '{"cycles": 1}'

# Get gamification profile
curl ${BACKEND_URL}/api/gamification/profile \\
  -H "Cookie: token=\${TOKEN}"`;

const MCP_CONFIG = `{
  "mcpServers": {
    "blackorex": {
      "command": "npx",
      "args": ["-y", "@blackorex/mcp-server"],
      "env": {
        "BLACKOREX_API_KEY": "\${YOUR_API_KEY}",
        "BLACKOREX_BASE_URL": "${BACKEND_URL}"
      }
    }
  }
}`;

const MCP_TOOLS = [
  { name: 'get_agents', desc: 'List all 17 AI agents with status, P&L, and performance metrics' },
  { name: 'get_signals', desc: 'Get latest trading signals with confidence scores and strategy metadata' },
  { name: 'get_journal', desc: 'Fetch trade journal with full explainability (reasoning, TCA metrics)' },
  { name: 'run_cycle', desc: 'Execute a trading engine cycle and return results' },
  { name: 'get_portfolio', desc: 'Get portfolio summary with net worth, allocations, and P&L breakdown' },
  { name: 'get_market_data', desc: 'Fetch real-time prices for crypto, stocks, ETFs, forex, IP assets' },
  { name: 'allocate_capital', desc: 'Assign capital from wallet to a specific AI agent' },
  { name: 'get_risk_dashboard', desc: 'Get risk metrics: VaR, Sharpe, drawdown, correlation matrix' },
];

function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <div className="flex items-center justify-between px-4 py-2 bg-[#0D1017] border-b border-white/5">
        <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{language}</span>
        <button onClick={handleCopy}
          className="flex items-center gap-1 text-[10px] font-mono text-slate-500 hover:text-white transition-colors"
          data-testid={`copy-${language}`}>
          {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? 'COPIED' : 'COPY'}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-[11px] font-mono leading-relaxed text-slate-300 bg-[#0A0D12] max-h-[400px] overflow-y-auto custom-scrollbar">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function SDKPage() {
  const { t } = useTranslation();
  const [activeSDK, setActiveSDK] = useState('python');

  return (
    <div data-testid="sdk-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#030508]/90 backdrop-blur-xl border-b border-white/[0.08] px-4 md:px-6 py-3">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-cyan-400" />
          <h1 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('sdk.title')}</h1>
          <span className="text-[10px] font-mono text-slate-500 bg-white/5 px-2 py-0.5">{t('sdk.devTools')}</span>
        </div>
      </div>

      <main className="p-4 md:p-6 space-y-8">
        {/* Hero */}
        <div className="bg-gradient-to-r from-cyan-500/10 via-[#090B0F] to-[#090B0F] border border-cyan-500/20 p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                Integra BlackOreX en tu stack
              </h2>
              <p className="text-sm text-slate-400 max-w-lg">
                Python SDK, JavaScript SDK, REST API, y MCP Server para conectar tus AI agents con nuestro engine de trading.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#030508] border border-white/5 p-3 text-center">
                <div className="text-lg font-black text-cyan-400">17</div>
                <div className="text-[9px] text-slate-500 uppercase">{t('landing.aiAgents')}</div>
              </div>
              <div className="bg-[#030508] border border-white/5 p-3 text-center">
                <div className="text-lg font-black text-cyan-400">8</div>
                <div className="text-[9px] text-slate-500 uppercase">{t('sdk.mcpToolsAvailable')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* SDK Tabs */}
        <div>
          <div className="flex gap-1 bg-[#090B0F] border border-white/10 p-1 mb-4" data-testid="sdk-tabs">
            {[
              { id: 'python', label: 'Python SDK', icon: Code, color: 'text-yellow-400 border-yellow-500' },
              { id: 'javascript', label: 'JavaScript SDK', icon: Terminal, color: 'text-amber-400 border-amber-500' },
              { id: 'curl', label: 'REST / cURL', icon: Globe, color: 'text-emerald-400 border-emerald-500' },
              { id: 'mcp', label: 'MCP Server', icon: Cpu, color: 'text-purple-400 border-purple-500' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveSDK(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 font-mono text-xs transition-all ${
                  activeSDK === tab.id ? `${tab.color} bg-white/5 border-b-2` : 'text-slate-500 hover:text-slate-300 border-b-2 border-transparent'
                }`}
                data-testid={`sdk-tab-${tab.id}`}>
                <tab.icon className="w-3.5 h-3.5" />{tab.label}
              </button>
            ))}
          </div>

          <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
            {activeSDK === 'python' && (
              <div>
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-sm font-bold text-white mb-1">{t('sdk.pythonSdk')}</h3>
                  <p className="text-xs text-slate-400">{t('sdk.installWith')}</p>
                  <div className="mt-3 flex gap-2">
                    <code className="text-[11px] font-mono bg-[#11141D] text-yellow-400 px-3 py-1.5 border border-white/5">pip install blackorex-sdk</code>
                  </div>
                </div>
                <CodeBlock code={SDK_PYTHON} language="python" />
              </div>
            )}

            {activeSDK === 'javascript' && (
              <div>
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-sm font-bold text-white mb-1">{t('sdk.jsSdk')}</h3>
                  <p className="text-xs text-slate-400">{t('sdk.jsSupport')}</p>
                  <div className="mt-3 flex gap-2">
                    <code className="text-[11px] font-mono bg-[#11141D] text-amber-400 px-3 py-1.5 border border-white/5">npm install @blackorex/sdk</code>
                  </div>
                </div>
                <CodeBlock code={SDK_JAVASCRIPT} language="javascript" />
              </div>
            )}

            {activeSDK === 'curl' && (
              <div>
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-sm font-bold text-white mb-1">{t('sdk.restCurl')}</h3>
                  <p className="text-xs text-slate-400">{t('sdk.restDirect')}</p>
                </div>
                <CodeBlock code={SDK_CURL} language="bash" />
              </div>
            )}

            {activeSDK === 'mcp' && (
              <div>
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-sm font-bold text-white mb-1">{t('sdk.mcpServer')}</h3>
                  <p className="text-xs text-slate-400">{t('sdk.mcpConnect')}</p>
                  <div className="mt-3 flex gap-2">
                    <code className="text-[11px] font-mono bg-[#11141D] text-purple-400 px-3 py-1.5 border border-white/5">npx @blackorex/mcp-server</code>
                  </div>
                </div>
                <CodeBlock code={MCP_CONFIG} language="json" />

                <div className="p-4 border-t border-white/10">
                  <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-purple-400" /> Available MCP Tools
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {MCP_TOOLS.map(tool => (
                      <div key={tool.name} className="flex items-start gap-2 bg-[#11141D] border border-white/5 p-3" data-testid={`mcp-tool-${tool.name}`}>
                        <ChevronRight className="w-3 h-3 text-purple-400 mt-0.5 shrink-0" />
                        <div>
                          <code className="text-[11px] font-mono text-purple-400">{tool.name}</code>
                          <p className="text-[10px] text-slate-500 mt-0.5">{tool.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Available Endpoints Summary */}
        <div className="bg-[#090B0F] border border-white/10 p-4">
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-emerald-500" /> API Endpoints
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            {[
              { method: 'GET', path: '/api/agents', desc: 'List all 17 agents' },
              { method: 'GET', path: '/api/stats', desc: 'Engine stats & P&L' },
              { method: 'GET', path: '/api/journal/trades', desc: 'Trade journal + TCA' },
              { method: 'GET', path: '/api/journal/signals', desc: 'Signal log' },
              { method: 'GET', path: '/api/wallet/balance', desc: 'Wallet balance' },
              { method: 'GET', path: '/api/wallet/portfolio-summary', desc: 'Portfolio summary' },
              { method: 'GET', path: '/api/risk/dashboard', desc: 'Risk metrics' },
              { method: 'GET', path: '/api/crypto/prices', desc: 'Crypto prices' },
              { method: 'GET', path: '/api/market/stocks', desc: 'US equities' },
              { method: 'GET', path: '/api/market/forex', desc: 'Forex rates' },
              { method: 'POST', path: '/api/trading/cycle', desc: 'Run engine cycle' },
              { method: 'POST', path: '/api/allocations/allocate', desc: 'Allocate capital' },
              { method: 'GET', path: '/api/gamification/profile', desc: 'Rewards profile' },
              { method: 'GET', path: '/api/gamification/quests', desc: 'Daily/weekly quests' },
            ].map(ep => (
              <div key={ep.path} className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.02] transition-colors">
                <span className={`text-[10px] font-mono font-bold w-10 ${ep.method === 'GET' ? 'text-emerald-400' : 'text-amber-400'}`}>{ep.method}</span>
                <code className="text-[11px] font-mono text-white flex-1">{ep.path}</code>
                <span className="text-[10px] text-slate-500">{ep.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
